import { useState, useEffect, useCallback, useMemo } from 'react';

const API_URL = (import.meta as unknown as { env: Record<string, string> }).env.VITE_API_URL ??
  (window.location.hostname === 'localhost' ? 'http://localhost:3001' : '');

interface SectorAnchor {
  id: string;
  industry: string;
  country: string;
  referenceYear: number;
  isProjection: boolean;
  activeFirms: number | null;
  avgRevenueEur: number | null;
  totalMarketSizeEur: number | null;
  employeesTotal: number | null;
  sourceName: string | null;
  sourceUrl: string | null;
  sourceYear: number | null;
  notes: string | null;
  lastRefreshedAt: string;
  updatedBy: string | null;
}

interface EditableAnchor {
  country: string;
  referenceYear: string;
  isProjection: boolean;
  activeFirms: string;
  avgRevenueEur: string;
  totalMarketSizeEur: string;
  employeesTotal: string;
  sourceName: string;
  sourceUrl: string;
  sourceYear: string;
  notes: string;
}

type EditTarget =
  | { mode: 'edit'; anchor: SectorAnchor }
  | { mode: 'create'; industry: string; defaultYear: number };

function authHeaders(): Record<string, string> {
  const token = sessionStorage.getItem('adminToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function toEditable(a: SectorAnchor): EditableAnchor {
  return {
    country: a.country ?? 'IT',
    referenceYear: a.referenceYear?.toString() ?? '',
    isProjection: a.isProjection ?? false,
    activeFirms: a.activeFirms?.toString() ?? '',
    avgRevenueEur: a.avgRevenueEur?.toString() ?? '',
    totalMarketSizeEur: a.totalMarketSizeEur?.toString() ?? '',
    employeesTotal: a.employeesTotal?.toString() ?? '',
    sourceName: a.sourceName ?? '',
    sourceUrl: a.sourceUrl ?? '',
    sourceYear: a.sourceYear?.toString() ?? '',
    notes: a.notes ?? '',
  };
}

function blankEditable(defaultYear: number): EditableAnchor {
  return {
    country: 'IT',
    referenceYear: defaultYear.toString(),
    isProjection: defaultYear > new Date().getUTCFullYear(),
    activeFirms: '',
    avgRevenueEur: '',
    totalMarketSizeEur: '',
    employeesTotal: '',
    sourceName: '',
    sourceUrl: '',
    sourceYear: defaultYear.toString(),
    notes: '',
  };
}

function parseNumOrNull(v: string): number | null {
  const trimmed = v.trim();
  if (!trimmed) return null;
  const n = Number(trimmed.replace(/[.,\s]/g, (ch) => (ch === ',' ? '.' : '')));
  return Number.isFinite(n) ? n : null;
}

function formatInt(n: number | null): string {
  if (n === null) return '—';
  return n.toLocaleString('it-IT');
}

function formatEur(n: number | null): string {
  if (n === null) return '—';
  if (n >= 1_000_000_000) return `€${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `€${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `€${(n / 1_000).toFixed(0)}k`;
  return `€${n}`;
}

function daysAgo(iso: string): string {
  const d = new Date(iso);
  const days = Math.floor((Date.now() - d.getTime()) / (24 * 60 * 60 * 1000));
  if (days === 0) return 'oggi';
  if (days === 1) return '1 giorno fa';
  if (days < 30) return `${days} giorni fa`;
  const months = Math.floor(days / 30);
  if (months === 1) return '1 mese fa';
  return `${months} mesi fa`;
}

export function SectorAnchorsPanel() {
  const [anchors, setAnchors] = useState<SectorAnchor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [target, setTarget] = useState<EditTarget | null>(null);
  const [form, setForm] = useState<EditableAnchor | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  const fetchAnchors = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/admin/sector-anchors`, {
        headers: { ...authHeaders() },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as SectorAnchor[];
      setAnchors(data);
      setError(null);
    } catch (err) {
      console.error(err);
      setError('Errore nel caricamento degli ancoraggi');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnchors();
  }, [fetchAnchors]);

  // Group rows by industry so the UI shows one section per sector with its year timeline.
  const grouped = useMemo(() => {
    const byIndustry = new Map<string, SectorAnchor[]>();
    for (const a of anchors) {
      const list = byIndustry.get(a.industry) ?? [];
      list.push(a);
      byIndustry.set(a.industry, list);
    }
    const result = Array.from(byIndustry.entries()).map(([industry, rows]) => ({
      industry,
      rows: rows.sort((x, y) => y.referenceYear - x.referenceYear),
    }));
    result.sort((a, b) => a.industry.localeCompare(b.industry));
    return result;
  }, [anchors]);

  function startEdit(a: SectorAnchor) {
    setTarget({ mode: 'edit', anchor: a });
    setForm(toEditable(a));
    setSaveError(null);
  }

  function startCreate(industry: string) {
    const existing = anchors
      .filter((a) => a.industry === industry)
      .map((a) => a.referenceYear);
    const currentYear = new Date().getUTCFullYear();
    // Suggest a year that isn't already stored for this industry.
    const candidate = existing.includes(currentYear) ? currentYear + 1 : currentYear;
    setTarget({ mode: 'create', industry, defaultYear: candidate });
    setForm(blankEditable(candidate));
    setSaveError(null);
  }

  function cancelEdit() {
    setTarget(null);
    setForm(null);
    setSaveError(null);
  }

  async function saveEdit() {
    if (!target || !form) return;
    const year = Number.parseInt(form.referenceYear, 10);
    if (!Number.isFinite(year) || year < 1990 || year > 2100) {
      setSaveError("Anno di riferimento non valido (deve essere tra 1990 e 2100).");
      return;
    }
    const industry = target.mode === 'edit' ? target.anchor.industry : target.industry;

    setSaving(true);
    setSaveError(null);
    try {
      const payload = {
        country: form.country.trim() || 'IT',
        referenceYear: year,
        isProjection: form.isProjection,
        activeFirms: parseNumOrNull(form.activeFirms),
        avgRevenueEur: parseNumOrNull(form.avgRevenueEur),
        totalMarketSizeEur: parseNumOrNull(form.totalMarketSizeEur),
        employeesTotal: parseNumOrNull(form.employeesTotal),
        sourceName: form.sourceName.trim() || null,
        sourceUrl: form.sourceUrl.trim() || null,
        sourceYear: parseNumOrNull(form.sourceYear),
        notes: form.notes.trim() || null,
      };
      const res = await fetch(
        `${API_URL}/api/admin/sector-anchors/${encodeURIComponent(industry)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      await fetchAnchors();
      cancelEdit();
    } catch (err) {
      setSaveError((err as Error).message || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  }

  async function deleteRow(a: SectorAnchor) {
    if (!confirm(`Eliminare la riga ${a.industry} · ${a.referenceYear}?`)) return;
    setDeleting(a.id);
    try {
      const res = await fetch(
        `${API_URL}/api/admin/sector-anchors/${encodeURIComponent(a.industry)}?year=${a.referenceYear}`,
        {
          method: 'DELETE',
          headers: { ...authHeaders() },
        }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await fetchAnchors();
    } catch (err) {
      console.error(err);
      alert('Errore nella cancellazione');
    } finally {
      setDeleting(null);
    }
  }

  if (loading) {
    return <div className="p-8 text-center text-gray-500">Caricamento...</div>;
  }

  return (
    <div className="p-4 lg:p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Ancoraggi di mercato</h2>
          <p className="text-sm text-gray-500 mt-1">
            Serie multi-anno per settore. Il trend è derivato comparando gli stessi indicatori
            su anni diversi — non salvare percentuali di crescita manualmente.
          </p>
        </div>
        <button
          onClick={fetchAnchors}
          className="text-sm text-gray-600 hover:text-gray-900 border border-gray-200 rounded px-3 py-1.5"
        >
          Ricarica
        </button>
      </div>

      {error && (
        <div className="bg-red-50 text-red-700 p-3 rounded-lg mb-3 text-sm">{error}</div>
      )}

      <div className="space-y-4">
        {grouped.map(({ industry, rows }) => {
          const latest = rows.find((r) => !r.isProjection) ?? rows[0];
          const hasCuratedData =
            latest &&
            (latest.activeFirms !== null ||
              latest.totalMarketSizeEur !== null ||
              latest.avgRevenueEur !== null);
          return (
            <div
              key={industry}
              className="bg-white rounded-lg border border-gray-200 overflow-hidden"
            >
              <div className="px-4 py-3 bg-gray-50 flex items-center justify-between border-b border-gray-200">
                <div>
                  <div className="font-medium text-gray-900">{industry}</div>
                  {!hasCuratedData && (
                    <div className="text-xs text-amber-600 mt-0.5">
                      dati mancanti — solo placeholder
                    </div>
                  )}
                </div>
                <button
                  onClick={() => startCreate(industry)}
                  className="text-xs text-primary-600 hover:underline"
                >
                  + Aggiungi anno
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-white text-gray-500 text-left">
                    <tr>
                      <th className="px-4 py-2 font-medium">Anno</th>
                      <th className="px-4 py-2 font-medium text-right">Imprese</th>
                      <th className="px-4 py-2 font-medium text-right">Ricavi medi</th>
                      <th className="px-4 py-2 font-medium text-right">TAM</th>
                      <th className="px-4 py-2 font-medium">Fonte</th>
                      <th className="px-4 py-2 font-medium">Aggiornato</th>
                      <th className="px-4 py-2 font-medium"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((a) => (
                      <tr key={a.id} className="border-t border-gray-100">
                        <td className="px-4 py-2 tabular-nums">
                          {a.referenceYear}
                          {a.isProjection && (
                            <span className="ml-2 text-xs px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded">
                              proiezione
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums">
                          {formatInt(a.activeFirms)}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums">
                          {formatEur(a.avgRevenueEur)}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums">
                          {formatEur(a.totalMarketSizeEur)}
                        </td>
                        <td className="px-4 py-2 text-gray-600">
                          {a.sourceName ? (
                            <>
                              {a.sourceName}
                              {a.sourceYear ? ` (${a.sourceYear})` : ''}
                            </>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-gray-500 text-xs">
                          {daysAgo(a.lastRefreshedAt)}
                        </td>
                        <td className="px-4 py-2 text-right whitespace-nowrap">
                          <button
                            onClick={() => startEdit(a)}
                            className="text-primary-600 hover:underline text-xs mr-3"
                          >
                            Modifica
                          </button>
                          <button
                            onClick={() => deleteRow(a)}
                            disabled={deleting === a.id}
                            className="text-red-600 hover:underline text-xs disabled:opacity-50"
                          >
                            Elimina
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}

        {grouped.length === 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-400">
            Nessun settore configurato.
          </div>
        )}
      </div>

      {target && form && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {target.mode === 'edit'
                  ? `Modifica: ${target.anchor.industry} · ${target.anchor.referenceYear}`
                  : `Nuovo anno: ${target.industry}`}
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                Lascia vuoto un campo per marcarlo come "non disponibile" nel report.
              </p>
            </div>

            <div className="px-6 py-4 space-y-4">
              {saveError && (
                <div className="bg-red-50 text-red-700 p-3 rounded text-sm">{saveError}</div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <Field
                  label="Anno di riferimento"
                  value={form.referenceYear}
                  onChange={(v) => setForm({ ...form, referenceYear: v })}
                  placeholder="es. 2024"
                  disabled={target.mode === 'edit'}
                />
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Proiezione (anno futuro)
                  </label>
                  <label className="flex items-center gap-2 text-sm py-2">
                    <input
                      type="checkbox"
                      checked={form.isProjection}
                      onChange={(e) =>
                        setForm({ ...form, isProjection: e.target.checked })
                      }
                    />
                    Questa riga è una proiezione
                  </label>
                </div>
                <Field
                  label="Paese"
                  value={form.country}
                  onChange={(v) => setForm({ ...form, country: v })}
                  placeholder="IT"
                />
                <Field
                  label="Imprese attive"
                  value={form.activeFirms}
                  onChange={(v) => setForm({ ...form, activeFirms: v })}
                  placeholder="es. 56234"
                />
                <Field
                  label="Ricavi medi per impresa (EUR)"
                  value={form.avgRevenueEur}
                  onChange={(v) => setForm({ ...form, avgRevenueEur: v })}
                  placeholder="es. 450000"
                />
                <Field
                  label="TAM settoriale totale (EUR)"
                  value={form.totalMarketSizeEur}
                  onChange={(v) => setForm({ ...form, totalMarketSizeEur: v })}
                  placeholder="es. 25000000000"
                />
                <Field
                  label="Addetti totali"
                  value={form.employeesTotal}
                  onChange={(v) => setForm({ ...form, employeesTotal: v })}
                  placeholder="es. 320000"
                />
                <Field
                  label="Anno della fonte"
                  value={form.sourceYear}
                  onChange={(v) => setForm({ ...form, sourceYear: v })}
                  placeholder="es. 2024"
                />
              </div>

              <Field
                label="Nome fonte"
                value={form.sourceName}
                onChange={(v) => setForm({ ...form, sourceName: v })}
                placeholder="es. ISTAT Movimprese"
              />
              <Field
                label="URL fonte"
                value={form.sourceUrl}
                onChange={(v) => setForm({ ...form, sourceUrl: v })}
                placeholder="https://..."
              />

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Note</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={3}
                  className="w-full border border-gray-200 rounded px-3 py-2 text-sm"
                  placeholder="Eventuali caveat o contesto"
                />
              </div>

              {target.mode === 'edit' && (
                <div className="text-xs text-gray-400">
                  Ultimo aggiornamento:{' '}
                  {new Date(target.anchor.lastRefreshedAt).toLocaleString('it-IT')}
                  {target.anchor.updatedBy ? ` · ${target.anchor.updatedBy}` : ''}
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={cancelEdit}
                disabled={saving}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
              >
                Annulla
              </button>
              <button
                onClick={saveEdit}
                disabled={saving}
                className="px-4 py-2 text-sm bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
              >
                {saving ? 'Salvataggio…' : 'Salva'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full border border-gray-200 rounded px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-500"
      />
    </div>
  );
}
